// A line chart as an instrument (docs/brand.md, Charts; Console D's lineChart): axes with units,
// hairline gridlines, a 2 px ink line and the last value annotated in ink. The accent (the
// periapsis colour) is opt-in, because a view has one accent at most. Further lines are steps of
// ink with a legend, never a hue each; a threshold is a dashed degraded line with its value; a
// release is a dashed vertical marker. It is drawn at its container's width, so the labels keep
// their size on a phone, and it is never taller than 220 px.
import { useEffect, useMemo, useRef, useState } from 'react'
import type { XKey } from './chart/state'
import {
  ChartAria,
  ChartFocus,
  ChartLegend,
  ChartOverlay,
  ChartTable,
  ChartTools,
  ChartTooltip,
  readout,
  useChart,
  useChartIds,
} from './chart/useChart'
import { useChartText } from './text'

export interface ChartPoint {
  label: string
  value: number | null
}

/** Another line over the same labels. */
export interface ChartSeries {
  name: string
  values: (number | null)[]
  /** Dashed, for a reference line such as the previous release's first days. */
  dash?: boolean | undefined
  /** A label at the line's last point ("5.1 99.71"), beside the plot. */
  end?: string | undefined
}

/** The tallest a chart gets (Console D: charts stop at 220 px whatever the window). */
export const MAX_HEIGHT = 220
const HEIGHT = 200
const PAD = { top: 12, right: 76, bottom: 24, left: 46 }

/** Rounded steps for the value axis. */
export function niceTicks(min: number, max: number, count = 5): number[] {
  if (min === max) {
    const d = Math.abs(min) * 0.001 || 0.001
    min -= d
    max += d
  }
  const raw = (max - min) / count
  const mag = 10 ** Math.floor(Math.log10(raw))
  const step = ([1, 2, 2.5, 5, 10].find((s) => s * mag >= raw) ?? 10) * mag
  // The top tick is at or above the largest value, so the line never runs over the plot
  // (300 to 420 ms reads 300 to 450, not 300 to 400 with the line past the top; APS-382).
  const end = Math.ceil(Number((max / step).toFixed(9))) * step
  const out: number[] = []
  for (let v = Math.floor(min / step) * step; v <= end + step / 2; v += step)
    out.push(Number(v.toFixed(10)))
  return out
}

/**
 * The value axis of a line chart: nice ticks over the values, where
 * - a flat series gets room around it (not five ticks a thousandth apart), and an all-zero one
 *   reads 0 at the base;
 * - values that are never negative (rates, counts, durations) never get a negative tick;
 * - two ticks that would print the same label are one.
 */
/**
 * `integer`: a count's axis (devices, sessions): whole-number ticks only, and a flat count padded
 * to the whole numbers around it (1 reads 0 / 1 / 2), never 0.995 to 1.005 (Mosaic, APS-324).
 */
export function axisTicks(
  values: number[],
  format: (v: number) => string,
  integer = false,
  /** A duration: the axis starts at 0 unless the values barely spread (APS-408). */
  fromZero = false,
): number[] {
  if (values.length === 0) return []
  let min = Math.min(...values)
  let max = Math.max(...values)
  // From 0, so a line's height reads as its duration; a nearly flat one keeps its fine scale.
  if (fromZero && min > 0 && max - min > max * 0.05) min = 0
  const floor = min >= 0 ? 0 : Number.NEGATIVE_INFINITY
  if (min === max) {
    if (integer) {
      min = Math.max(floor, min - 1)
      max += 1
    } else if (min === 0) max = 1
    else {
      // A little room around one value, so the line sits mid-chart on a fine scale (a crash-free
      // 66.67% reads 66.4 to 67, not 65 to 68; Mosaic, APS-305).
      const d = Math.abs(min) * 0.005
      min -= d
      max += d
    }
  }
  const ticks = niceTicks(Math.max(floor, min), max, 4).filter(
    (t) => t >= floor && (!integer || Number.isInteger(t)),
  )
  const out: number[] = []
  for (const t of ticks) {
    const prev = out.at(-1)
    if (prev === undefined || axisLabel(format(prev)) !== axisLabel(format(t))) out.push(t)
  }
  // A single printed label is no axis: keep the base and the top.
  if (out.length < 2 && ticks.length >= 2) return [ticks[0] as number, ticks.at(-1) as number]
  return out
}

/**
 * An axis label: the value's own format without trailing zeros ("75.00%" reads "75%", "1.20 s"
 * reads "1.2 s"), as the mock's axes print as many decimals as the step needs and no more. A
 * group of exactly three digits is a thousands group ("1,050 ms", "1.050 ms"), never trimmed.
 */
export function axisLabel(text: string): string {
  return text.replace(/(?<=\d)[.,](\d+)(?!\d)/g, (all, digits: string) => {
    if (digits.length === 3) return all
    const kept = digits.replace(/0+$/, '')
    return kept ? `${all[0]}${kept}` : ''
  })
}

/**
 * The labels of a value axis, trimmed together: every label keeps the decimals the finest one
 * needs, so "67%" never sits next to "66.8%" (it reads "67.0%"; Mosaic, APS-305). A group of
 * exactly three digits is a thousands group, as in axisLabel.
 */
export function axisLabels(texts: string[]): string[] {
  const group = /(?<=\d)[.,](\d+)(?!\d)/g
  let keep = 0
  for (const text of texts)
    for (const m of text.matchAll(group)) {
      const digits = m[1] ?? ''
      if (digits.length !== 3) keep = Math.max(keep, digits.replace(/0+$/, '').length)
    }
  return texts.map((text) =>
    text.replace(group, (all, digits: string) => {
      if (digits.length === 3) return all
      const kept = digits.slice(0, keep)
      return kept ? `${all[0]}${kept}` : ''
    }),
  )
}

/**
 * The labels of a value axis with the unit written once, on the top tick ("1100 ms", "1050",
 * "1000"), as on the mock's axes; labels with different units are left whole.
 */
export function unitOnTop(labels: string[]): string[] {
  const split = labels.map((l) => /^([−+-]?[\d.,\s]*\d)(\s*[^\d\s].*)$/.exec(l))
  const unit = split[0]?.[2]
  if (labels.length < 2 || !unit || split.some((m) => m?.[2] !== unit)) return labels
  return split.map((m, i) => (i === labels.length - 1 ? labels[i] : m?.[1]) ?? '')
}

/** A label beside a line's last point. */
export interface EndLabel {
  key: string
  x: number
  y: number
  text: string
  className: string
}

/** The height a label takes, and the gap two labels on the same column keep (Mosaic, APS-307). */
const LABEL_GAP = 12

/**
 * End labels that never overprint: in order of height, a label that would sit within 12 px of
 * one already placed over the same columns moves below it; a label pushed past the bottom moves
 * the column's labels up instead.
 */
export function placeEndLabels(
  labels: readonly EndLabel[],
  bottom: number,
  /** Labels that stay where they are (a threshold's), which the others keep clear of. */
  fixed: readonly EndLabel[] = [],
): EndLabel[] {
  const width = (l: EndLabel) => l.text.length * 6.4
  const overlaps = (a: EndLabel, b: EndLabel) => a.x < b.x + width(b) && b.x < a.x + width(a)
  const placed: EndLabel[] = [...fixed]
  for (const l of [...labels].sort((a, b) => a.y - b.y)) {
    let y = l.y
    for (let guard = 0; guard < labels.length; guard++) {
      const hit = placed.find((p) => overlaps(p, l) && Math.abs(p.y - y) < LABEL_GAP)
      if (!hit) break
      y = hit.y + LABEL_GAP
    }
    placed.push({ ...l, y })
  }
  const moved = placed.slice(fixed.length)
  const over = Math.max(0, ...moved.map((p) => p.y - bottom))
  return over > 0 ? moved.map((p) => ({ ...p, y: p.y - over })) : moved
}

export function useWidth(fallback: number) {
  const ref = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(fallback)
  useEffect(() => {
    const el = ref.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(([entry]) => {
      if (entry) setWidth(Math.max(280, Math.round(entry.contentRect.width)))
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  return [ref, width] as const
}

export function LineChart({
  points,
  format,
  label,
  marker,
  lastLabel,
  more = [],
  name,
  threshold,
  unit,
  accent = false,
  endsInGutter = false,
  legend = false,
  markerLegend,
  boxLegend = false,
  area = false,
  integer = false,
  fromZero = false,
  span,
  endLabelsFrom,
  height = HEIGHT,
  keys,
  timeAxis = false,
  onPick,
  pickHint,
  table = true,
  xName,
  titles,
  tipFormat,
}: {
  points: ChartPoint[]
  format: (v: number) => string
  /** What the chart shows, for screen readers. */
  label: string
  /** A dashed vertical line at a point, for example a release shipping. */
  marker?: { index: number; label: string } | undefined
  lastLabel?: (p: ChartPoint) => string
  /** Further lines over the same labels (at most 5 more), with a legend. */
  more?: ChartSeries[] | undefined
  /** The first line's name, for the legend when there are several. */
  name?: string | undefined
  /** A budget or an alert's limit: a dashed horizontal line with its label. */
  threshold?: { value: number; label: string } | undefined
  /** The axis unit, over the value labels (`%`, `ms`). */
  unit?: string | undefined
  /** The last point in the accent: only when nothing else on the view has it. */
  accent?: boolean | undefined
  /**
   * End labels in the right gutter, stacked, never over a line (Mosaic, APS-307): for lines that
   * end at different points, such as releases by days since they shipped.
   */
  endsInGutter?: boolean | undefined
  /** A legend even for one line (Screen detail, APS-392); further lines always have one. */
  legend?: boolean | undefined
  /** The marker's legend item, as the mock's "┆ 5.2 (812) shipped Sep 29". */
  markerLegend?: string | undefined
  /** Square swatches in the legend, as the mock's line charts with an end label per line. */
  boxLegend?: boolean | undefined
  /** A light ink area under the first line, as the mock's rate charts have. */
  area?: boolean | undefined
  /** A count: whole-number ticks only (axisTicks). */
  integer?: boolean | undefined
  /** A duration: the value axis starts at 0 unless the values barely spread (APS-408). */
  fromZero?: boolean | undefined
  /**
   * Values the axis always covers, as a share's 0 to 100 % (an SLO's budget left, Console D):
   * the line's fall then reads at its true size. Values outside it still widen the axis.
   */
  span?: readonly [number, number] | undefined
  /**
   * End labels only from this width up; narrower, the legend names the lines and the plot takes
   * the width (Mosaic, APS-528: on a phone, three end labels left the plot 45 px wide).
   */
  endLabelsFrom?: number | undefined
  height?: number | undefined
  /**
   * Each point's lasting identity (a sample's time), for a live chart whose window slides: a zoom
   * stays on its samples as new ones arrive (APS-577). The index when unset.
   */
  keys?: readonly XKey[] | undefined
  /** The x axis is time: a drag zooms into a span, Reset zoom undoes it (APS-577). */
  timeAxis?: boolean | undefined
  /** A click (or Enter) on a point opens a filtered list: the point's index and series. */
  onPick?: ((index: number, series: number) => void) | undefined
  /** What a click on that point opens, as the tooltip's last line; none: it opens nothing. */
  pickHint?: ((index: number, series: number) => string | undefined) | undefined
  /** The Table toggle; off where the view already shows these values as a table. */
  table?: boolean | undefined
  /** The table's first column (the x axis's name). */
  xName?: string | undefined
  /**
   * Each point's full name, for its tooltip, the table and the zoom status, where the axis label
   * is short ("7" on a days-since-ship axis reads "day 7"). The point's label when unset.
   */
  titles?: readonly string[] | undefined
  /**
   * A value as the tooltip and the table say it, with its noun ("3 calls"), where the axis's
   * format is a bare number (Mosaic). The axis format when unset.
   */
  tipFormat?: ((v: number) => string) | undefined
}) {
  const [ref, width] = useWidth(760)
  const ids = useChartIds()
  const text = useChartText()
  const allKeys = useMemo(() => keys ?? points.map((_, i) => i), [keys, points])
  const seriesCount = 1 + more.length
  const zoomable = timeAxis && points.length > 2
  const names = [name ?? '', ...more.map((s) => s.name)]
  const chart = useChart({
    keys: allKeys,
    names,
    zoomable,
    // Index keys mean nothing across data: the labels say which days these are.
    data: keys ? undefined : points.map((p) => p.label).join('\u001f'),
  })
  const { from, to, isHidden } = chart
  // What the chart shows: the zoomed span of each series, hidden series left out.
  const shown = points.slice(from, to + 1)
  const shownMore = more.map((s) => ({ ...s, values: s.values.slice(from, to + 1) }))
  const mainOn = !isHidden(0)
  // A series' end label belongs to its last value: while zoomed short of the end, none shows.
  const atEnd = to === points.length - 1
  const shownMarker =
    marker && marker.index >= from && marker.index <= to
      ? { ...marker, index: marker.index - from }
      : undefined
  const h = Math.min(height, MAX_HEIGHT)
  const values = [
    ...(mainOn ? shown.map((p) => p.value) : []),
    ...shownMore.flatMap((s, i) => (isHidden(i + 1) ? [] : s.values)),
    ...(threshold ? [threshold.value] : []),
    ...(span ?? []),
  ].filter((v): v is number => v !== null)
  const w = width
  const narrow = w < 480
  const ticks = axisTicks(values, format, integer, fromZero)
  const tickLabels = unitOnTop(axisLabels(ticks.map(format)))
  const longest = Math.max(0, ...tickLabels.map((l) => l.length))
  const lastIndex = shown.findLastIndex((p) => p.value !== null)
  const last = lastIndex >= 0 ? shown[lastIndex] : undefined
  const showEnds = (endLabelsFrom === undefined || w >= endLabelsFrom) && atEnd
  const lastText =
    showEnds && mainOn && last && last.value !== null
      ? lastLabel
        ? lastLabel(last)
        : format(last.value)
      : ''
  // The right gutter holds the end labels whole at any width, on a phone too (APS-382). It keeps
  // its width while a series is hidden or the chart zoomed, so the plot does not jump.
  const fullLast = points.findLast((p) => p.value !== null)
  const fullLastText =
    (endLabelsFrom === undefined || w >= endLabelsFrom) && fullLast && fullLast.value !== null
      ? lastLabel
        ? lastLabel(fullLast)
        : format(fullLast.value)
      : ''
  const endChars =
    endLabelsFrom === undefined || w >= endLabelsFrom
      ? Math.max(fullLastText.length, ...more.map((s) => s.end?.length ?? 0))
      : 0
  const gutter = endChars > 0 ? Math.round(endChars * 6.4 + 14) : 16
  const pad = {
    ...PAD,
    // A release marker's label gets a row of its own above the plot, clear of the line.
    top: marker ? PAD.top + 12 : PAD.top,
    left: Math.max(PAD.left, Math.round(longest * 6.4 + 12)),
    right: narrow ? gutter : Math.max(PAD.right, gutter),
  }
  const plotW = w - pad.left - pad.right
  const plotH = h - pad.top - pad.bottom
  const lo = ticks[0] ?? 0
  const hi = ticks.at(-1) ?? 1
  const x = (i: number) => pad.left + (shown.length > 1 ? (i / (shown.length - 1)) * plotW : plotW)
  const y = (v: number) => pad.top + plotH - ((v - lo) / (hi - lo || 1)) * plotH
  const path = (list: (number | null)[]) => {
    let d = ''
    let pen = false
    list.forEach((v, i) => {
      if (v === null) {
        pen = false
        return
      }
      d += `${pen ? 'L' : 'M'} ${x(i).toFixed(1)} ${y(v).toFixed(1)} `
      pen = true
    })
    return d.trim()
  }
  const d = mainOn ? path(shown.map((p) => p.value)) : ''
  // x labels at least ~64 px apart, or a label's width if wider: never touching. The last one
  // always shows, ending at its point, so a label whose half (the first's whole) and the last's
  // whole do not fit between them gives way (Mosaic, APS-528: '25 out. 26 out.').
  const step = shown.length > 1 ? plotW / (shown.length - 1) : plotW
  const widest = Math.max(0, ...shown.map((p) => p.label.length)) * 6.6 + 12
  const minGap = Math.max(64, widest)
  const labelEvery = Math.max(
    1,
    Math.ceil(shown.length / (narrow ? 3 : 6)),
    Math.ceil(minGap / Math.max(1, step)),
  )
  // The last point's label and each further line's end label, kept apart.
  const ends = placeEndLabels(
    [
      ...(lastText && last && last.value !== null
        ? [
            {
              key: 'last',
              x: endsInGutter ? w - pad.right + 8 : x(lastIndex) + 9,
              y: y(last.value) + 4,
              text: lastText,
              className: 'last-t',
            },
          ]
        : []),
      ...shownMore.flatMap((s, i) => {
        const at = s.values.findLastIndex((v) => v !== null)
        const v = s.values[at]
        return showEnds && !isHidden(i + 1) && s.end && v !== null && v !== undefined
          ? [
              {
                key: `end${i}`,
                x: endsInGutter ? w - pad.right + 8 : x(at) + 9,
                y: y(v) + 4,
                text: s.end,
                className: 'end-t',
              },
            ]
          : []
      }),
    ],
    h - 2,
    // The threshold's label sits in the gutter too, beside its line.
    threshold && endsInGutter && !narrow
      ? [
          {
            key: 'thr',
            x: w - pad.right + 6,
            y: y(threshold.value) + 3.5,
            text: threshold.label,
            className: 'thr-t',
          },
        ]
      : [],
  )

  // The interaction layer (APS-577): the active point's values, every shown series at that x.
  const xs = shown.map((_, i) => x(i))
  const seriesValues = (i: number): (number | null | undefined)[] => [
    shown[i]?.value,
    ...shownMore.map((s) => s.values[i]),
  ]
  // A series opens something when the caller says so: with pickHint, the series it names a
  // target for (Health exits opens crashed sessions from its crash line only); without, every one.
  const opens = (i: number, s: number) =>
    !isHidden(s) && (!pickHint || pickHint(from + i, s) !== undefined)
  /** The series a click or Enter at that point opens, or -1: from the keyboard the first that
   * opens; from a pointer, the nearest one that opens. */
  const target = (i: number, py: number | null): number => {
    const vals = seriesValues(i)
    let s = -1
    let best = Number.POSITIVE_INFINITY
    vals.forEach((v, k) => {
      if (v === null || v === undefined || !opens(i, k)) return
      const dist = py === null ? k : Math.abs(y(v) - py)
      if (dist < best) {
        best = dist
        s = k
      }
    })
    return s
  }
  const pick = onPick
    ? (i: number, at: { x: number; y: number } | null) => {
        const s = target(i, at?.y ?? null)
        if (s >= 0) onPick(from + i, s)
      }
    : undefined
  const a = chart.active
  const rowsAt = (i: number) =>
    seriesValues(i).flatMap((v, s) =>
      isHidden(s) || v === undefined
        ? []
        : [
            {
              series: s,
              name: seriesCount > 1 ? (names[s] ?? '') : '',
              value: v === null ? text.none : (tipFormat ?? format)(v),
            },
          ],
    )
  const rows = a >= 0 ? rowsAt(a) : []
  const at = a >= 0 && onPick ? target(a, null) : -1
  const hint = at >= 0 ? pickHint?.(from + a, at) : undefined
  const titleOf = (i: number) => titles?.[from + i] ?? shown[i]?.label ?? ''
  const title = a >= 0 ? titleOf(a) : ''
  const say = a >= 0 ? readout(title, rows, hint) : ''
  const shownNames = names.filter((_, s) => !isHidden(s))

  return (
    <div ref={ref} className="ch-wrap">
      <ChartFocus
        chart={chart}
        label={label}
        ids={ids}
        allKeys={allKeys}
        zoomable={zoomable}
        onPick={pick}
        width={w}
      >
        <svg
          className="chart"
          viewBox={`0 0 ${w} ${h}`}
          width="100%"
          height={h}
          role="img"
          aria-label={label}
        >
          {ticks.map((t, i) => (
            <g key={t}>
              <path className="grid" d={`M ${pad.left} ${y(t)} H ${w - pad.right}`} />
              <text className="ax" x={pad.left - 6} y={y(t) + 3.5} textAnchor="end">
                {tickLabels[i]}
              </text>
            </g>
          ))}
          {unit ? (
            <text className="ax" x={pad.left - 6} y={pad.top - 2} textAnchor="end">
              {unit}
            </text>
          ) : null}
          <path className="base" d={`M ${pad.left} ${pad.top + plotH} H ${w - pad.right}`} />
          {shown.map((p, i) =>
            (i % labelEvery === 0 &&
              (shown.length - 1 - i) * step >= Math.max(minGap, (i === 0 ? 2 : 1.5) * widest)) ||
            i === shown.length - 1 ? (
              <text
                // Labels can repeat (hours of two days): the position keeps the key unique.
                // biome-ignore lint/suspicious/noArrayIndexKey: points never reorder
                key={i}
                className="ax"
                x={x(i)}
                y={h - 7}
                // The last label ends inside the plot, even when it is the only one (Mosaic, APS-307).
                textAnchor={i === shown.length - 1 ? 'end' : i === 0 ? 'start' : 'middle'}
              >
                {p.label}
              </text>
            ) : null,
          )}
          {shownMarker && shown[shownMarker.index] ? (
            <g>
              <path
                className="mk"
                d={`M ${x(shownMarker.index)} ${pad.top} V ${pad.top + plotH}`}
              />
              <text
                className="mk-t"
                x={x(shownMarker.index) + (shownMarker.index > shown.length / 2 ? -5 : 5)}
                // In its own row above the plot, so it never sits on the top gridline or the line.
                y={pad.top - 8}
                textAnchor={shownMarker.index > shown.length / 2 ? 'end' : 'start'}
              >
                {shownMarker.label}
              </text>
            </g>
          ) : null}
          {threshold ? (
            <g>
              <path className="thr" d={`M ${pad.left} ${y(threshold.value)} H ${w - pad.right}`} />
              {narrow ? (
                <text
                  className="thr-t"
                  x={w - pad.right}
                  y={y(threshold.value) - 5}
                  textAnchor="end"
                >
                  {threshold.label}
                </text>
              ) : (
                <text className="thr-t" x={w - pad.right + 6} y={y(threshold.value) + 3.5}>
                  {threshold.label}
                </text>
              )}
            </g>
          ) : null}
          {shownMore.map((s, i) =>
            isHidden(i + 1) ? null : (
              // biome-ignore lint/suspicious/noArrayIndexKey: series keep their order
              <g key={i}>
                <path className={`line s${i + 1}${s.dash ? ' dash' : ''}`} d={path(s.values)} />
              </g>
            ),
          )}
          {area && d ? (
            <path
              className="area"
              d={(() => {
                const at = shown
                  .map((p, i) => (p.value === null ? null : ([x(i), y(p.value)] as const)))
                  .filter((v): v is readonly [number, number] => v !== null)
                const base = y(lo)
                const first = at[0]
                const lastAt = at.at(-1)
                if (!first || !lastAt) return ''
                return `M ${first[0].toFixed(1)} ${base.toFixed(1)} ${at
                  .map(([px, py]) => `L ${px.toFixed(1)} ${py.toFixed(1)}`)
                  .join(' ')} L ${lastAt[0].toFixed(1)} ${base.toFixed(1)} Z`
              })()}
            />
          ) : null}
          {d ? <path className="line" d={d} /> : null}
          {mainOn && atEnd && last && last.value !== null ? (
            <g>
              <circle
                className={accent ? 'lastp acc' : 'lastp'}
                cx={x(lastIndex)}
                cy={y(last.value)}
                r="4.5"
              />
            </g>
          ) : null}
          {ends.map((l) => (
            <text key={l.key} className={l.className} x={l.x} y={l.y}>
              {l.text}
            </text>
          ))}
          <ChartOverlay
            chart={chart}
            xs={xs}
            allKeys={allKeys}
            width={w}
            left={pad.left}
            right={w - pad.right}
            top={pad.top}
            bottom={pad.top + plotH}
            crosshair
            zoomable={zoomable}
            onPick={pick}
          />
          {a >= 0
            ? seriesValues(a).map((v, s) =>
                v === null || v === undefined || isHidden(s) ? null : (
                  <circle
                    // biome-ignore lint/suspicious/noArrayIndexKey: series keep their order
                    key={s}
                    className={`ch-dot s${s}`}
                    cx={xs[a]}
                    cy={y(v)}
                    r="4"
                  />
                ),
              )
            : null}
        </svg>
      </ChartFocus>
      {a >= 0 && xs[a] !== undefined ? (
        <ChartTooltip
          x={xs[a] as number}
          width={w}
          top={pad.top}
          title={title}
          rows={rows}
          hint={hint}
        />
      ) : null}
      <ChartAria ids={ids} chart={chart} text={say} canOpen={!!onPick} canZoom={zoomable} />
      {more.length > 0 || legend || (marker && markerLegend) ? (
        <ChartLegend
          names={names}
          box={boxLegend}
          marker={marker && markerLegend ? markerLegend : undefined}
          hidden={chart.hidden}
          onToggle={more.length > 0 ? chart.toggle : undefined}
        />
      ) : null}
      <ChartTools
        chart={chart}
        table={table}
        zoomedFrom={titleOf(0)}
        zoomedTo={titleOf(shown.length - 1)}
        tableId={ids.table}
      />
      {table && chart.table ? (
        <ChartTable
          id={ids.table}
          label={label}
          xName={xName}
          // A short head: the chart's name is long and pushed the values out of sight (Mosaic).
          series={seriesCount > 1 ? shownNames : [name || text.value]}
          rows={shown.map((_, i) => ({
            label: titleOf(i),
            values: seriesValues(i).flatMap((v, s) =>
              isHidden(s)
                ? []
                : [v === null || v === undefined ? text.none : (tipFormat ?? format)(v)],
            ),
          }))}
        />
      ) : null}
    </div>
  )
}

// The interaction layer's parts, for a chart drawn by hand (APS-577): the console reaches them
// through this entry, as it does the line chart.
export type { XKey } from './chart/state'
export {
  type ChartApi,
  ChartAria,
  ChartFocus,
  ChartLegend,
  ChartOverlay,
  ChartTable,
  ChartTools,
  ChartTooltip,
  chartKeys,
  LegendToggle,
  readout,
  type TooltipRow,
  useChart,
  useChartIds,
} from './chart/useChart'
